/**
 * Inline report-block parser.
 *
 * The report planner agent finalizes a report-generating turn by appending a
 * JSON block of the form
 *
 *     {"type": "report", "report": {"id": 56, "title": "...", "url": "/56", "run_id": "..."}}
 *
 * into the assistant's text content (see `_finalize_output_text` in
 * `mfour-agents/workers/planner/agent.py`). The block is rendered in the chat
 * as a "Open report" call-to-action card by `content-renderer.tsx`. Anything
 * before/after the block is preserved as plain text so the assistant can talk
 * about the report in the same turn.
 *
 * Brace-counting (with escaped-quote tracking) keeps stringified JSON inside
 * the payload from prematurely closing the outermost object.
 */

export interface ReportBlock {
    id: number;
    title: string;
    runId?: string;
}

export type ReportSegment =
    | { kind: 'text'; text: string }
    | { kind: 'report'; report: ReportBlock };

/**
 * Find the index just past the matching `}` for the `{` at `text[start]`.
 * Returns -1 if the object is unbalanced.
 */
function findJsonObjectEnd(text: string, start: number): number {
    let depth = 0;
    let inString = false;
    let escape = false;
    for (let i = start; i < text.length; i++) {
        const ch = text[i];
        if (escape) {
            escape = false;
            continue;
        }
        if (ch === '\\' && inString) {
            escape = true;
            continue;
        }
        if (ch === '"') {
            inString = !inString;
            continue;
        }
        if (inString) continue;
        if (ch === '{') depth++;
        else if (ch === '}') {
            depth--;
            if (depth === 0) return i + 1;
        }
    }
    return -1;
}

function tryParseReportAt(
    text: string,
    start: number,
): { report: ReportBlock; end: number } | null {
    const end = findJsonObjectEnd(text, start);
    if (end === -1) return null;
    try {
        const obj = JSON.parse(text.slice(start, end));
        if (obj?.type !== 'report' || !obj?.report) return null;
        const id = Number(obj.report.id);
        if (!Number.isFinite(id) || id <= 0) return null;
        return {
            report: {
                id,
                title: typeof obj.report.title === 'string' ? obj.report.title : 'Report',
                runId: typeof obj.report.run_id === 'string' ? obj.report.run_id : undefined,
            },
            end,
        };
    } catch {
        return null;
    }
}

/**
 * Split a string into ordered text/report segments. Empty text segments are
 * dropped so consumers can map directly to React elements without filtering.
 */
export function parseReportSegments(text: string): ReportSegment[] {
    if (!text || !text.includes('"type"') || !text.includes('"report"')) {
        return text ? [{ kind: 'text', text }] : [];
    }

    const segments: ReportSegment[] = [];
    let cursor = 0;

    while (cursor < text.length) {
        const idx = text.indexOf('{', cursor);
        if (idx === -1) {
            const tail = text.slice(cursor);
            if (tail.trim().length > 0) segments.push({ kind: 'text', text: tail });
            break;
        }

        // Cheap pre-filter: only attempt the full JSON parse when the
        // candidate object's first 80 chars look like a report block.
        const peek = text.slice(idx, idx + 80);
        const looksLikeReport = peek.includes('"type"') && peek.includes('"report"');

        const parsed = looksLikeReport ? tryParseReportAt(text, idx) : null;
        if (parsed) {
            const before = text.slice(cursor, idx).replace(/\s+$/, '');
            if (before.length > 0) segments.push({ kind: 'text', text: before });
            segments.push({ kind: 'report', report: parsed.report });
            cursor = parsed.end;
            // Eat trailing whitespace so we don't render a hairline gap.
            while (
                cursor < text.length &&
                (text[cursor] === '\n' || text[cursor] === '\r' || text[cursor] === ' ')
            ) {
                cursor++;
            }
            continue;
        }

        const nextIdx = text.indexOf('{', idx + 1);
        if (nextIdx === -1) {
            const tail = text.slice(cursor);
            if (tail.trim().length > 0) segments.push({ kind: 'text', text: tail });
            break;
        }
        const chunk = text.slice(cursor, nextIdx);
        if (chunk.trim().length > 0) segments.push({ kind: 'text', text: chunk });
        cursor = nextIdx;
    }

    return segments;
}
